import { useEffect, useMemo, useState } from "react";

import { Button, Modal, Select, Spinner } from "@shapr3d/frontend-lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { defaultTeamspaceName, queryKeys } from "../../../config";
import { TeamMembersQueryKey, useTeamMembers } from "../../../hooks/useTeams";
import { type TeamSpace, useTeamSpaces } from "../../../hooks/useTeamSpaces";
import type { TeamsUsersResponseData } from "../../../hooks/useTeamsTypes";
import { useTracker } from "../../../hooks/useTracker";
import { ElementClickAction, ElementClickLocation } from "../../../lib/TrackingAdapter";
import { UMSrequestClient } from "../../../stores/requestClient";

import style from "./RemoveFromSpaceModal.module.css";

const useMutateRemoveFromSpace = ({ onSuccess }: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  const { teamID } = useParams();

  return useMutation({
    onSuccess: (_, variables) => {
      queryClient.setQueryData<TeamsUsersResponseData | undefined>(
        [...TeamMembersQueryKey, teamID],
        (oldData) => {
          const updatedUsers = oldData?.users
            .filter((user) => variables.userIDs.includes(user.userId ?? ""))
            .map((user) => {
              return {
                ...user,
                spaces: [
                  ...(user.spaces?.filter((space) => space.id !== variables.selectedSpaceID) ?? []),
                ],
              };
            });

          if (oldData) {
            return {
              users: [
                ...oldData.users.filter((user) => !variables.userIDs.includes(user.userId ?? "")),
                ...(updatedUsers ?? []),
              ],
            };
          }
          return oldData;
        }
      );
      onSuccess?.();
    },
    mutationKey: [...queryKeys.authenticatedQueryKey, "remove-from-teamspace"],
    mutationFn: ({
      selectedSpaceID,
      userIDs,
    }: {
      selectedSpaceID: string | undefined;
      selectedSpaceName: string | undefined;
      userIDs: string[];
    }) => {
      return UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: `/teams/${teamID}/spaces/${selectedSpaceID}/users/remove`,
        body: {
          users: userIDs.map((userID) => ({ id: userID })),
        },
      });
    },
  });
};

export const RemoveFromSpaceModal = ({
  open,
  onClose,
  userIDs,
}: {
  open: boolean;
  onClose: () => void;
  userIDs: string[];
}) => {
  const { teamID } = useParams();
  const tracker = useTracker();

  const { data: teamMembersData } = useTeamMembers(teamID);
  const selectedSpaceIDs = useMemo(() => {
    return (
      teamMembersData?.users
        ?.filter((member) => userIDs.includes(member.userId ?? ""))
        .map((member) => member.spaces)
        .flat()
        .map((space) => space?.id) ?? []
    );
  }, [userIDs, teamMembersData]);

  const { data: spacesData } = useTeamSpaces(teamID);
  const filteredSpaces = useMemo(() => {
    return spacesData?.spaces.filter((space) => selectedSpaceIDs.includes(space.id));
  }, [spacesData, selectedSpaceIDs]);

  const {
    mutate: mutateRemoveFromSpace,
    isPending,
    isError,
  } = useMutateRemoveFromSpace({
    onSuccess: onClose,
  });

  const [selectedSpace, setSelectedSpace] = useState<TeamSpace | undefined>();

  useEffect(() => {
    if (open && filteredSpaces) {
      setSelectedSpace(filteredSpaces[0]);
    }
  }, [open, filteredSpaces]);

  return (
    <Modal
      title={`Remove ${userIDs.length} member${userIDs.length > 1 ? "s" : ""} from`}
      open={open}
      onClose={onClose}
      size="small"
    >
      {!filteredSpaces && <Spinner />}
      {!filteredSpaces && <div>No spaces found</div>}
      {filteredSpaces && (
        <div className={style.modalContentContainer}>
          <Select
            defaultValue={filteredSpaces?.[0]?.id}
            onChange={(e) => {
              setSelectedSpace(filteredSpaces.find((item) => item.id === e.target.value));
              tracker.trackButtonClick({
                action: ElementClickAction.SELECT_SPACE,
                details: e.target.value,
                location: ElementClickLocation.REMOVE_FROM_SPACE_MODAL,
              });
            }}
          >
            {filteredSpaces.map(({ id, name }) => (
              <option key={id} value={id}>
                {name ?? defaultTeamspaceName}
              </option>
            ))}
          </Select>
          <Button
            loading={isPending}
            onClick={async () => {
              mutateRemoveFromSpace({
                selectedSpaceID: selectedSpace?.id,
                selectedSpaceName: selectedSpace?.name,
                userIDs,
              });
              tracker.trackButtonClick({
                action: ElementClickAction.CONFIRM,
                location: ElementClickLocation.REMOVE_FROM_SPACE_MODAL,
              });
            }}
          >
            Remove from space
          </Button>
          {isError && <div className={style.error}>Something went wrong.</div>}
        </div>
      )}
    </Modal>
  );
};
